import { ITEM_TYPE_STORY } from "../../components/Card/constants";
import * as CONSTANTS from "./constants";
import { VIDEO_CANONICAL_BASE_PATH } from "../../constants/videos";
import { makeURL, removeUrlDomain, stripBetaPrefix } from "../path/library";
import {
    createPackageComponents,
    getSlug,
    filterContentItem,
    getDepartmentHierarchy,
    isItemExternal,
    replaceWithLargerDerivative,
    createCardIDs,
    getDepartmentsFromURL,
    isSearchItemExternal,
    getContextualOverridesBySlug,
    getBaseSectionFromURL,
} from "./utils";
import { attributionStringToObject } from "../../actions/Tracking/utils";
import { getStoryPageJsonLdSchema } from "../seo/storyPage";

export function transformTrendingList(contentList) {
    const transformedData = contentList
        .filter((item) => filterContentItem(item))
        .map((item) => transformTrendingItem(item));
    return transformedData;
}

/**
 * Transforms the allContentItems response from graphQL schema to application format
 *
 * @param {Object[]} allContentItems
 * @returns {Object[]}
 */
export function transformAllContentItems(
    allContentItems,
    contextualOverrideSlug = ""
) {
    return allContentItems.map((contentItem) => {
        const urlCategory = getDepartmentHierarchy(contentItem.sectionList);

        let isExternal = isItemExternal(contentItem, urlCategory);

        let imageURL = replaceWithLargerDerivative(contentItem.imageLarge);

        let url = isExternal
            ? contentItem.url
            : removeUrlDomain(contentItem.url);

        let flag = contentItem.flag;

        let flags = {
            status: contentItem.flags?.status,
            label: contentItem.flags?.label,
        };

        let title = contentItem.title;

        if (contextualOverrideSlug) {
            const contextualOverride = getContextualOverridesBySlug(
                contextualOverrideSlug,
                contentItem.contextualOverrides
            );
            if (contextualOverride) {
                if (contextualOverride.headline) {
                    title = contextualOverride.headline;
                }
                if (contextualOverride.flag) {
                    flags.label = contextualOverride.flag;
                }
            }
        }

        let formattedAuthor;
        if (contentItem.authors?.length > 0) {
            const image = contentItem?.authors?.[0]?.photo?.aspects?.ratio1x1
                ?.url
                ? `${contentItem.authors[0].photo.aspects.ratio1x1.url}Resize%3D114`
                : "";
            formattedAuthor = {
                name: contentItem.authors[0].name,
                image,
            };
        }

        let videoId = null,
            videoDuration = null,
            videoCardOverlayType = "",
            videoTitle = "",
            videoAirDate = null,
            videoHasCaptions = false,
            videoStreamType = null;

        if (!videoId && contentItem.type === "video") {
            videoCardOverlayType = CONSTANTS.NORMAL;
            videoId = contentItem.sourceId ?? contentItem.mediaId;
            videoDuration = contentItem.media?.duration ?? null;
            videoAirDate = Number(contentItem.publishedAt);
            videoHasCaptions = contentItem.media?.hasCaptions ?? false;
            videoStreamType = contentItem.media?.streamType ?? null;
        }

        if (videoId && contentItem.type === "video") {
            url = `${VIDEO_CANONICAL_BASE_PATH}video/${videoId}`;
        }

        if (
            videoCardOverlayType === CONSTANTS.NORMAL &&
            flag.includes(CONSTANTS.COMING_UP)
        ) {
            flag = "";
        }

        // Legacy data in media might pass these strings as values in showName,
        // this functions ensures these strings are
        // handled as "null" values
        const cleanShowName = ["(not applicable)", "(not specified)"].includes(
            contentItem.showName?.toLowerCase()
        )
            ? null
            : contentItem.showName;

        const cardIds = createCardIDs("card", contentItem.sourceId);

        // WPP-872 Stop gap since live stories do not currently have a description field
        const descriptionToUse =
            contentItem.type === "livestory"
                ? contentItem.deck
                : contentItem.description;

        return {
            id: contentItem.id,
            contentId: contentItem.id,
            url: url,
            itemURL: contentItem.url,
            departments: {
                sectionList: contentItem.sectionList,
                sectionLabels: contentItem.sectionLabels,
            },
            category: contentItem.sectionList
                ? contentItem.sectionList[contentItem.sectionList.length - 1]
                : "",
            relatedLinks: contentItem.relatedLinks,
            description: descriptionToUse,
            flag: flag,
            flags: flags,
            imageURL: imageURL,
            image: contentItem.image || { url: contentItem?.imageLarge }, //this is needed because MOF fetchSubjects call comes back with image as undefined and then image:{url:""} is the format needed by CQW cards
            insertAd: contentItem.insertAd ? contentItem.insertAd : undefined,
            publishTime: Number(contentItem.publishedAt),
            updateTime: Number(contentItem.updatedAt),
            sourceId: contentItem.sourceId,
            source: contentItem.source,
            slug: getSlug(url),
            title: title,
            itemType: contentItem.type,
            show: cleanShowName,
            author: formattedAuthor,
            displayComments: contentItem.commentsEnabled,
            videoId: videoId,
            videoCardOverlayType: videoCardOverlayType,
            videoDuration: videoDuration,
            videoAirDate: videoAirDate,
            videoTitle: videoTitle,
            videoHasCaptions: videoHasCaptions,
            videoStreamType: videoStreamType,
            external: isExternal,
            ...cardIds,
            ...transformContentPackage(contentItem),
        };
    });
}

/**
 * Transforms the allContentItems component object to application format
 *
 * @param {Object} contentItem the content item being mapped over
 * @returns {Object}
 */
export function transformContentPackage(contentItem) {
    if (!contentItem || !contentItem.components) {
        return;
    }
    const components = contentItem?.components;
    const { mainContent, mainVisual } = components;
    const slug = getSlug(mainContent.url);
    const sourceId = mainContent.sourceId;
    const itemURL = mainContent.url;
    const sectionList = mainContent.sectionList;
    const urlCategory = getDepartmentHierarchy(sectionList);
    const external =
        mainContent.type === "stub"
            ? true
            : isItemExternal(contentItem, urlCategory);
    const url = makeURL(external, itemURL, { urlCategory, slug, sourceId });
    const videoId = mainVisual?.sourceId ?? mainVisual?.mediaId;
    const videoAirDate = Number(mainVisual?.publishedAt) ?? null;
    const videoDuration = mainVisual?.media?.duration ?? null;
    const videoTitle = mainVisual?.title ?? "";
    const videoStreamType = mainVisual?.media?.streamType ?? null;
    const imageLarge =
        (mainVisual && mainVisual.imageLarge) ||
        contentItem.imageLarge ||
        mainContent.imageLarge;
    const imageURL =
        (imageLarge && replaceWithLargerDerivative(imageLarge)) || "";
    const description = !contentItem.description ? contentItem.deck : "";
    const packageItem = {
        slug,
        description,
        sourceId,
        contentId: parseFloat(sourceId),
        itemURL,
        sectionList,
        urlCategory,
        url,
        packageComponents: createPackageComponents(components),
        videoId,
        videoAirDate,
        videoDuration,
        videoTitle,
        videoStreamType,
        external,
        imageURL,
    };

    // Clean any undefined fields so they don't overwrite content item properties
    return Object.keys(packageItem).reduce((acc, key) => {
        if (packageItem[key] !== undefined) acc[key] = packageItem[key];
        return acc;
    }, {});
}

export function transformSearchContentList(contentList) {
    const transformedData = contentList.map((item) =>
        transformSearchContentItem(item)
    );
    return transformedData;
}

export function transformGQLSearchContentList(contentList) {
    const transformedData = contentList.map((item) =>
        transformGQLSearchItem(item)
    );
    return transformedData;
}

export function transformAutocompleteList(contentList) {
    const transformedData = contentList.map((item, index) =>
        transformAutocompleteItem(item, index)
    );
    return transformedData;
}

/**
 * Takes raw item data and transforms it to expected application format.
 *
 * @param {Object} contentItem (REQUIRED) Raw item data.
 *
 * @returns {Object} transformed data item.
 */
export function transformTrendingItem(contentItem) {
    const sourceId = contentItem.sourceId;
    const imageURL = contentItem.image;
    const title = contentItem.title;
    const url = stripBetaPrefix(contentItem.href);
    const departments = getDepartmentsFromURL(url);
    const flag = contentItem.flag;
    const rank = contentItem.rank;
    const count = contentItem.count;
    const countSrs = contentItem.countSrs;
    // CardDiscovery which is used to display TrendingList items requires a showImage prop
    const showImage = false;
    const variant = contentItem.variant;

    const transformedItem = {
        sourceId,
        imageURL,
        title,
        url,
        departments,
        flag,
        itemType: ITEM_TYPE_STORY,
        external: false,
        rank,
        count,
        countSrs,
        showImage,
        variant,
    };

    return transformedItem;
}

export function transformAuthorContentList(contentList) {
    const transformedData = contentList.map((item) =>
        transformAuthorItem(item)
    );
    return transformedData;
}

export function transformContentPackageItem(
    contentItem,
    transformedContentItem
) {
    const packageComponent = contentItem.typeAttributes?.components;
    let deck = "";

    if (!packageComponent) {
        return transformedContentItem;
    }

    if (!contentItem.description) {
        deck = contentItem.typeAttributes?.deck;
    }

    const mainContent = contentItem.typeAttributes.components.mainContent || {};

    const itemURL = mainContent.typeAttributes?.url;

    const slug = getSlug(mainContent.typeAttributes?.url);
    const sectionList = mainContent.typeAttributes?.sectionList;
    const sourceId = mainContent.sourceId;
    const urlCategory = getDepartmentHierarchy(sectionList);

    let external = isItemExternal(contentItem, urlCategory);
    const isStub = mainContent.type;
    if (isStub === "stub") {
        external = true;
    }

    const url = makeURL(external, itemURL, { urlCategory, slug, sourceId });
    const uppercaseHeadline = contentItem.typeAttributes.uppercaseHeadline;

    const mainVisual = contentItem.typeAttributes.components.mainVisual || {};

    const mainVisualType = mainVisual.type;
    const videoId = mainVisualType === "video" ? mainVisual.sourceId : null;

    const videoDuration =
        mainVisualType === "video"
            ? mainVisual.typeAttributes?.mediaDuration
            : null;

    const videoTitle = mainVisualType === "video" ? mainVisual.title : null;

    return {
        ...transformedContentItem,
        slug,
        sourceId,
        itemURL,
        sectionList,
        urlCategory,
        url,
        packageComponents: packageComponent,
        uppercaseHeadline,
        description: deck,
        videoId,
        videoDuration,
        videoTitle,
        external,
    };
}

export function transformAuthorItem(contentItem) {
    const originalId = contentItem.id;

    // id needs some processing, author item ids come as strings, not as numbers
    const id =
        typeof originalId === "string"
            ? Number(originalId.replace(/[^0-9]+/g, ""))
            : originalId;

    // Section dependent values
    let sectionList = contentItem.sectionList || [];
    let sectionLabels = contentItem.sectionLabels || [];

    const jsonDepts = contentItem.categories || [];
    if (sectionList.length === 0 || sectionLabels.length === 0) {
        sectionList = [];
        sectionLabels = [];
        jsonDepts.forEach((item) => {
            sectionList.push(item.slug);
            sectionLabels.push(item.name);
        });
    }

    const urlCategory = getDepartmentHierarchy(sectionList);
    const external = isItemExternal(contentItem, urlCategory);
    const category =
        !sectionList || sectionList.length === 0
            ? ""
            : sectionList[sectionList.length - 1];
    const departments = {
        sectionLabels: sectionLabels,
        sectionList: sectionList,
    };
    const contentId = Number(originalId);
    const description = contentItem.description;
    const flag = contentItem.flag;

    const imageURL = replaceWithLargerDerivative(contentItem.imageLarge);

    const source = contentItem.source;

    const publishTime = Number(contentItem.publishedAt);
    const sourceId = `${originalId}`;
    const title = contentItem.title;
    const itemType = contentItem.type;
    const slug = getSlug(contentItem.url);
    const updateTime = Number(contentItem.updatedAt);
    const itemURL = contentItem.url;
    const url = itemURL;

    const transformedItem = {
        id,
        external,
        url,
        category,
        departments,
        contentId,
        description,
        flag,
        imageURL,
        publishTime,
        source,
        sourceId,
        slug,
        title,
        itemType,
        updateTime,
        itemURL,
    };

    return transformedItem;
}

export function transformAutocompleteItem(contentItem, index) {
    const value = contentItem.value;
    const id = `autocomplete-item${index}`;

    const transformedItem = {
        value,
        id,
    };

    return transformedItem;
}

export function transformSearchContentItem(contentItem) {
    const typeAttributes = contentItem.typeAttributes || {};
    const id = contentItem.id;
    const sectionList = typeAttributes.sectionList;
    const category = contentItem.category0;
    const description = contentItem.description;
    const flag = typeAttributes.flag;
    const author = typeAttributes.author;
    const departments = {
        sectionLabels: typeAttributes.sectionLabels,
        sectionList: sectionList,
    };
    const relatedLinks = typeAttributes.relatedLinks;
    // Get biggest variation possible
    const imageURL = contentItem.dominantimage;

    const publishTime = contentItem.publishtime
        ? new Date(contentItem.publishtime).getTime()
        : 0;
    const source = "Polopoly";
    const sourceId = contentItem.sourceId;
    const title = contentItem.title;
    const slug = getSlug(typeAttributes.url);
    const itemType = contentItem["cbc-mediaid"] ? "video" : "story";
    const updateTime = publishTime;
    const itemURL = typeAttributes.url;
    const focus = contentItem.focus;
    const insertAd = contentItem.insertAd;
    const show = typeAttributes.show;

    let url = contentItem.url;
    const external = isSearchItemExternal(url);

    if (!external) {
        url = removeUrlDomain(url);
    }

    let videoCardOverlayType = "";

    if (itemType === "video") {
        videoCardOverlayType = CONSTANTS.NORMAL;
    }

    const transformedItem = {
        id,
        external,
        url,
        category,
        departments,
        relatedLinks,
        description,
        flag,
        imageURL,
        publishTime,
        source,
        sourceId,
        slug,
        title,
        itemType,
        updateTime,
        itemURL,
        focus,
        insertAd,
        show,
        author,
        videoCardOverlayType,
    };

    return transformedItem;
}

/**
 * Takes the elastic search document GQL response and transforms it to the structure that contentArea uses
 *
 * @param {Object} searchItem the response returned from GQL
 */

export const transformGQLSearchItem = (searchItem) => {
    const itemURL = searchItem.url;
    const external = isSearchItemExternal(itemURL);
    const slug = getSlug(itemURL);
    const category = getBaseSectionFromURL(itemURL);

    const transformedItem = {
        id: searchItem.sourceId,
        author: searchItem.authors,
        category: category,
        description: searchItem.description,
        external: external,
        flag: searchItem.flags?.status,
        imageURL: searchItem.imageUrl,
        itemType: searchItem.contentType,
        itemURL: itemURL,
        publishTime: parseInt(searchItem.publishedAt) ?? 0,
        show: searchItem.showName,
        slug: slug,
        source: searchItem.source,
        sourceId: searchItem.sourceId,
        title: searchItem.title,
        updateTime: parseInt(searchItem.updatedAt) ?? 0,
        url: external ? searchItem.url : removeUrlDomain(searchItem.url),
        videoCardOverlayType:
            searchItem.contentType === "video" ? CONSTANTS.NORMAL : "",
    };
    return transformedItem;
};

/**
 * Takes the content item GQL response and transforms it to the structure that detail uses
 *
 * @param {Object} response the response returned from GQL
 * @param {string} contentId the content id of the story
 */
export const transformGQLResponseToDetailStructure = (response, contentId) => {
    const schema = getStoryPageJsonLdSchema(response);

    return {
        id: contentId,
        deck: response.deck,
        advertising: response.advertising,
        attribution: attributionStringToObject(
            response.section?.attributionLevels
        ),
        authorDisplay: response.authorDisplay,
        authors: response.authors,
        tags: response.tags,
        concepts: response.concepts,
        body: response.body?.parsed || [],
        byline: response.byline,
        clarifications: response.clarifications,
        corrections: response.corrections,
        departments: response.categories,
        displaycomments: response.commentsEnabled,
        description: response.description,
        episode: response.segment?.episode || null,
        episodemedia: response.episode?.media || null,
        externalLinks: response.externalLinks,
        flag: response.flag,
        fbpages: response.section?.social?.facebookPages,
        hasaudio: response.body?.containsAudio,
        hasvideo: response.body?.containsVideo,
        hasgallery: response.body?.containsPhotogallery,
        headline: response.title,
        highlights: response.highlights,
        image: response.image,
        intlinks: response.intlinks,
        language: response.language,
        leadMedia: response.leadMedia,
        mediaid: response.mediaid,
        newsletter: response.newsletter,
        photoGallery: response.photoGallery,
        publishedAt: response.publishedAt,
        schema: schema ?? response.jsonLD ?? {},
        segments: response.episode?.segments || null,
        segmentmedia: response.segment?.media || null,
        shareHeadline: response.shareHeadline,
        source: response.editorialSource,
        sponsor: response.sponsor,
        tracking: response.section?.tracking || {},
        type: response.type,
        updatedAt: response.updatedAt,
        url: response.url || "",
        vfsection: response.section?.social?.commentsSection,
        wordcount: response.wordcount,
    };
};

export const transformScheduleList = (schedule = []) => {
    return schedule.map((item) => {
        const uuid = encodeURIComponent(
            `key-${item.stt}-${item.end}-${item.ti}`
        );
        return { ...item, uuid };
    });
};

export const transformRelatedItems = (response) => {
    const relatedItems = response.data?.relatedItems?.nodes?.map((item) => {
        return {
            title: item.title,
            url: item.url,
        };
    });
    return relatedItems ? relatedItems : [];
};
